import React, { useState, useEffect } from 'react';
import {
  X,
  Edit2,
  Archive,
  Play,
  Pause,
  Clock,
  Target,
  Bell,
  Shield,
  AlertTriangle,
  Mail,
  MessageSquare,
  UserCheck,
  CheckCircle,
  XCircle,
  ChevronRight
} from 'lucide-react';

/**
 * PolicyDetailDrawer Component
 *
 * Slide-in drawer showing policy details.
 * Styled to match PolicyWizard modal patterns.
 * Includes slide-in/slide-out animation.
 */

const PolicyDetailDrawer = ({ policy, isOpen, onClose, onEdit, onArchive, onToggleStatus }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cachedPolicy, setCachedPolicy] = useState(null);

  // Cache the policy when opening so we can show it during close animation
  useEffect(() => {
    if (policy && isOpen) {
      setCachedPolicy(policy);
    }
  }, [policy, isOpen]);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      // Opening: mount first, then animate in
      setIsAnimating(true);
      // Small delay to ensure mount happens before animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else if (isAnimating) {
      // Closing: animate out first, then unmount
      setIsVisible(false);
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setCachedPolicy(null);
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Don't render if not animating
  if (!isAnimating) return null;

  // Use cached policy during close animation
  const displayPolicy = policy || cachedPolicy;
  if (!displayPolicy) return null;

  const {
    name,
    description = null,
    status = 'draft',
    scope = {},
    metric = {},
    actions = [],
    last_triggered_at = null,
    trigger_count_24h = 0,
    recent_events = [],
    active_environments = []
  } = displayPolicy;

  // Status badge config
  const statusConfig = {
    active: { colorClass: 'text-status-success bg-status-success/20', label: 'Active', icon: Play },
    draft: { colorClass: 'text-text-muted bg-text-muted/20', label: 'Draft', icon: Pause },
    archived: { colorClass: 'text-text-muted bg-text-muted/20', label: 'Archived', icon: Archive }
  };

  const statusInfo = statusConfig[status] || statusConfig.draft;
  const StatusIcon = statusInfo.icon;

  // Action type icons
  const actionIcons = {
    respond_gracefully: { icon: Shield, label: 'Graceful Response' },
    raise_error: { icon: AlertTriangle, label: 'Raise Error' },
    notify_email: { icon: Mail, label: 'Email Notification' },
    notify_slack: { icon: MessageSquare, label: 'Slack Notification' },
    human_in_loop: { icon: UserCheck, label: 'Human Review' }
  };

  // Format metric condition
  const formatMetricCondition = (metric) => {
    if (!metric?.type) return '-';

    const typeLabels = {
      trace_latency_p95: 'P95 Latency',
      trace_latency_p99: 'P99 Latency',
      trace_latency_avg: 'Average Latency',
      quality_score: 'Quality Score',
      quality_p20_pct: 'Poor Quality %',
      quality_p40_pct: 'Low Quality %',
      error_rate: 'Error Rate',
      security_violations: 'Security Violations',
      cost_per_trace: 'Cost per Trace'
    };

    const comparatorLabels = {
      '>': '>',
      '>=': '≥',
      '<': '<',
      '<=': '≤',
      '==': '='
    };

    const typeLabel = typeLabels[metric.type] || metric.type;
    const comparator = comparatorLabels[metric.comparator] || metric.comparator || '>';
    const threshold = metric.threshold != null ? metric.threshold : '?';
    const unit = metric.type?.includes('latency') ? 'ms' : '';

    return `${typeLabel} ${comparator} ${threshold}${unit}`;
  };

  // Format scope
  const formatScope = (scope) => {
    const parts = [];
    if (scope.environment) parts.push(scope.environment);
    if (scope.app_name || scope.app_id) parts.push(scope.app_name || `App ${scope.app_id}`);
    return parts.length > 0 ? parts.join(' / ') : 'All';
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString();
    } catch {
      return '-';
    }
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '-';

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      return `${diffDays}d ago`;
    } catch {
      return '-';
    }
  };

  return (
    <>
      {/* Backdrop - starts below header to not block dropdowns */}
      <div
        className={`fixed top-16 left-0 right-0 bottom-0 z-40 transition-opacity duration-300 ${
          isVisible ? 'bg-black/50' : 'bg-black/0'
        }`}
        onClick={onClose}
      />

      {/* Drawer - starts below header, slides in from right */}
      <div
        className={`fixed top-16 right-0 bottom-0 w-full max-w-md z-50 flex flex-col bg-background-secondary border-l border-border-subtle shadow-2xl transition-transform duration-300 ease-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-text-primary">
              Policy Details
            </h2>
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${statusInfo.colorClass}`}>
              <StatusIcon size={12} />
              {statusInfo.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Policy Name & Description */}
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-1">
              {name}
            </h3>
            {description && (
              <p className="text-sm text-text-supporting">{description}</p>
            )}
          </div>

          {/* Scope & Active Environments */}
          <div>
            <div className="text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-2">
              Scope
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Target size={16} className="text-text-muted" />
              <span className="text-text-primary">{formatScope(scope)}</span>
            </div>
            {active_environments.length > 0 ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-text-muted">Active in:</span>
                {active_environments.map((env) => (
                  <span
                    key={env}
                    className="px-2 py-0.5 rounded text-xs bg-status-success/20 text-status-success"
                  >
                    {env}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-sm text-text-muted">
                Not active in any environment
              </div>
            )}
          </div>

          {/* Metric Condition */}
          <div>
            <div className="text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-2">
              Condition
            </div>
            <div className="p-4 bg-surface-overlay/50 rounded-lg border border-border-subtle">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={16} className="text-text-muted" />
                <span className="font-medium text-text-primary">
                  {formatMetricCondition(metric)}
                </span>
              </div>
              <div className="text-sm text-text-supporting">
                Window: {metric.window || '-'}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div>
            <div className="text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-2">
              Actions ({actions.length})
            </div>
            <div className="space-y-2">
              {actions.length > 0 ? (
                actions.map((action, idx) => {
                  const actionInfo = actionIcons[action.type] || { icon: Bell, label: action.type };
                  const ActionIcon = actionInfo.icon;
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-surface-overlay/50 rounded-lg border border-border-subtle"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded bg-accent-primary-base/20 text-accent-primary-base">
                        <ActionIcon size={16} />
                      </div>
                      <div>
                        <div className="font-medium text-text-primary">
                          {actionInfo.label}
                        </div>
                        {action.config?.value && (
                          <div className="text-sm text-text-muted">
                            {action.config.value}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-text-muted">
                  No actions configured
                </div>
              )}
            </div>
          </div>

          {/* Trigger Stats */}
          <div>
            <div className="text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-2">
              Trigger Activity
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-surface-overlay/50 rounded-lg border border-border-subtle">
                <div className="text-sm text-text-muted">Last Triggered</div>
                <div className="font-medium text-text-primary">
                  {formatRelativeTime(last_triggered_at)}
                </div>
              </div>
              <div className="p-3 bg-surface-overlay/50 rounded-lg border border-border-subtle">
                <div className="text-sm text-text-muted">Triggers (24h)</div>
                <div className="font-medium text-text-primary">
                  {trigger_count_24h}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Events */}
          {recent_events && recent_events.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-2">
                Recent Events
              </div>
              <div className="space-y-2">
                {recent_events.slice(0, 5).map((event, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2 bg-surface-overlay/50 rounded-lg border border-border-subtle"
                  >
                    {event.status === 'success' ? (
                      <CheckCircle size={16} className="text-status-success" />
                    ) : (
                      <XCircle size={16} className="text-status-danger" />
                    )}
                    <div className="flex-1">
                      <div className="text-sm text-text-primary">
                        {event.actions_executed?.length || 0} actions executed
                      </div>
                      <div className="text-xs text-text-muted">
                        {formatTimestamp(event.triggered_at)}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-text-muted" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle bg-background-secondary">
          <div className="flex items-center gap-2">
            {status !== 'archived' && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1 px-3 py-2 text-sm text-status-danger hover:bg-status-danger/20 rounded transition-colors"
              >
                <Archive size={14} />
                Archive
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {status !== 'archived' && (
              <>
                <button
                  onClick={() => onToggleStatus?.(displayPolicy)}
                  className={`flex items-center gap-1 px-3 py-2 text-sm rounded transition-colors ${
                    status === 'active'
                      ? 'text-status-warning hover:bg-status-warning/20'
                      : 'text-status-success hover:bg-status-success/20'
                  }`}
                >
                  {status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                  {status === 'active'
                    ? `Pause (${active_environments.length} env${active_environments.length !== 1 ? 's' : ''})`
                    : 'Activate'}
                </button>
                <button
                  onClick={() => onEdit?.(displayPolicy)}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium rounded bg-accent-primary-base text-background-primary hover:opacity-90 transition-opacity"
                >
                  <Edit2 size={14} />
                  Edit
                </button>
              </>
            )}
          </div>
        </div>

        {/* Archive Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-10">
            <div className="bg-background-secondary border border-border-subtle rounded-lg p-6 max-w-sm w-full">
              <h3 className="font-semibold text-text-primary mb-2">
                Archive Policy?
              </h3>
              <p className="text-sm text-text-supporting mb-4">
                This policy will be archived and will no longer trigger actions. You can restore it later from the Archived tab.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onArchive?.(displayPolicy);
                    setShowDeleteConfirm(false);
                    onClose();
                  }}
                  className="px-4 py-2 text-sm font-medium rounded bg-status-danger text-white hover:opacity-90 transition-opacity"
                >
                  Archive
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PolicyDetailDrawer;
